import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Button,
  Grid,
} from "@material-ui/core";
import { Card, CardHeader } from "reactstrap";

import { notify } from "../../utils/notify";
import ModalLoading from "../../components/modalLoading/ModalLoading";
import { UserContext } from "../../context/UserContext";
import {
  deleteButtonPointAccess,
  getButtonPointAccesss,
} from "../../repositories/buttonPointAccessRepository";
import SearchInput from "../../components/basic/SearchInput";
import filterTable from "../../utils/filterTable";

function createData(Id, Nome, Largura, Altura, Funcao, Preview, Cor, CorTexto) {
  return {
    Id,
    Nome,
    Largura,
    Altura,
    Funcao,
    Preview,
    Cor,
    CorTexto,
  };
}

const headCells = [
  {
    name: "Id",
    numeric: false,
    disablePadding: false,
    label: "Id",
    sort: true,
  },
  {
    name: "Nome",
    numeric: false,
    disablePadding: false,
    label: "Nome",
    sort: true,
  },
  {
    name: "Largura",
    numeric: false,
    disablePadding: false,
    label: "Largura",
    sort: false,
  },
  {
    name: "Altura",
    numeric: false,
    disablePadding: false,
    label: "Altura",
    sort: false,
  },
  {
    name: "Funcao",
    numeric: false,
    disablePadding: false,
    label: "Função",
    sort: false,
  },
  {
    name: "Preview",
    numeric: false,
    disablePadding: false,
    label: "Preview",
    sort: false,
  },
  {
    name: "Acoes",
    numeric: false,
    disablePadding: false,
    label: "Ações",
    sort: false,
  },
];

function EnhancedTableHead() {
  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell key={headCell.name} padding={"default"}>
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  table: {
    minWidth: 750,
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
  row: {
    margin: "1px",
  },
}));

const PreviewButtonPointAccess = () => {
  const { user } = useContext(UserContext);
  const [actionsPermission, setActionsPermission] = useState({
    delete: false,
    edit: false,
  });
  const history = useHistory();
  const classes = useStyles();
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("Id");
  const [selected, setSelected] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [rowsFilter, setRowsFilter] = useState([]);

  useEffect(() => {
    if (user && user.accessRoutes) {
      const actionPermission = {
        delete: user.accessRoutes.includes("/deleteButtonPointAccess"),
        edit: user.accessRoutes.includes("/updateButtonPointAccess"),
      };

      setActionsPermission(actionPermission);
    }
  }, [user]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const { data: response } = await getButtonPointAccesss();

        const employees = response.data.map((employee) => {
          return createData(
            employee.Id,
            employee.Nome,
            employee.Largura,
            employee.Altura,
            employee.Funcao,
            "",
            employee.Cor,
            employee.CorTexto
          );
        });
        setRows(employees);
        setRowsFilter(employees);
        setAllRows(response.data);
        setLoading(false);
      } catch (error) {
        console.error("ERROR: PreviewButtonPointAccess", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleRequestSort = (_, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = rows.map((n) => n.nome);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

  const handleNewInsert = () => {
    history.push("/createButtonPointAccess");
  };

  const handleDelete = async (Id) => {
    if (window.confirm(`Você realmente quer apagar o registro de Id: ${Id}`)) {
      const rowDelete = allRows.find((rw) => rw.Id === Id);

      if (rowDelete) {
        try {
          setLoading(true);

          const deleteItem = await deleteButtonPointAccess({ id: Id });

          if (deleteItem) {
            const removeItem = rows.filter((item) => item.Id !== Id);
            setRows(removeItem);
            setRowsFilter(removeItem);
            notify("Registro deletado com sucesso.", true, "info");
          }
          setLoading(false);
        } catch (error) {
          console.error("ERROR: DeleteButtonPointAccess", error);
          setLoading(false);
        }
      } else {
        notify("Registro não encontrado.");
      }
    }
  };

  const handleEdit = (Id) => {
    const rowEdit = allRows.find((rw) => rw.Id === Id);
    if (rowEdit) {
      history.push("/updateButtonPointAccess", { row: rowEdit });
    } else {
      notify("Registro não encontrado.");
    }
  };

  const handleChangeFilter = (e) => {
    if (!e || !e.target.value) return setRowsFilter(allRows);

    const newArr = filterTable({
      data: allRows,
      text: e.target.value,
      columns: ["Nome", "Funcao"],
    });

    setRowsFilter(newArr);
    setPage(0);
  };

  return (
    <>
      {loading && <ModalLoading loading={loading} />}

      <div>
        <div className="containerTable">
          <Card className="card-default">
            <CardHeader className={classes.row}>
              <Grid container alignItems="center" spacing={1}>
                <Grid item lg={4} md={6} sm={12} xs={12}>
                  <label className="title">Botões de ponto de acesso</label>
                </Grid>

                <Grid item lg={6} md={6} sm={12} xs={12}>
                  <SearchInput
                    placeholder="Pesquisar"
                    handleChange={handleChangeFilter}
                  />
                </Grid>

                <Grid
                  container
                  justify="flex-end"
                  alignItems="center"
                  item
                  lg={2}
                  md={12}
                  sm={12}
                  xs={12}
                >
                  <Grid item>
                    <Button
                      variant="contained"
                      size="large"
                      id="buttonInserir"
                      onClick={handleNewInsert}
                    >
                      <img
                        src="img/inserir.svg"
                        className="iconInserir"
                        alt="Inserir"
                      />
                      INSERIR
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </CardHeader>
            <hr />
            <TableContainer>
              <Table
                className={classes.table}
                aria-labelledby="tableTitle"
                size="medium"
                aria-label="enhanced table"
              >
                <EnhancedTableHead
                  classes={classes}
                  numSelected={selected.length}
                  order={order}
                  orderBy={orderBy}
                  onSelectAllClick={handleSelectAllClick}
                  onRequestSort={handleRequestSort}
                  rowCount={rowsFilter.length}
                />
                <TableBody>
                  {rowsFilter
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {
                      return (
                        <TableRow hover tabIndex={-1} key={index}>
                          <TableCell align="left">{row.Id}</TableCell>
                          <TableCell align="left">{row.Nome}</TableCell>
                          <TableCell align="left">{row.Largura}</TableCell>
                          <TableCell align="left">{row.Altura}</TableCell>
                          <TableCell align="left">{row.Funcao}</TableCell>
                          <TableCell align="left">
                            {
                              <Button
                                variant="outlined"
                                disableElevation
                                style={{
                                  background: row.Cor,
                                  color: row.CorTexto,
                                  textTransform: "none",
                                }}
                              >
                                {row.Nome}
                              </Button>
                            }
                          </TableCell>
                          <TableCell align="left">
                            {actionsPermission.edit && (
                              <Tooltip title="Editar">
                                <IconButton
                                  aria-label="edit"
                                  onClick={() => handleEdit(row.Id)}
                                >
                                  <img
                                    alt="Editar"
                                    src="img/acoes.svg"
                                    className="iconAtivo"
                                  />
                                </IconButton>
                              </Tooltip>
                            )}
                            {actionsPermission.delete && (
                              <Tooltip title="Deletar">
                                <IconButton
                                  aria-label="delete"
                                  onClick={() => handleDelete(row.Id)}
                                >
                                  <img
                                    alt="Deletar"
                                    src="img/excluir.svg"
                                    className="iconAtivo"
                                  />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={9} />
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25]}
              component="div"
              count={rowsFilter.length}
              rowsPerPage={rowsPerPage}
              page={page}
              labelRowsPerPage={"Linhas por página"}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </Card>
        </div>
      </div>
    </>
  );
};

export default PreviewButtonPointAccess;
